<template>
    <!-- Modal de recherche de notifications -->
    <b-modal :visible="visible" @hide="onHide" centered no-close-on-backdrop no-close-on-esc 
    dialog-class="fullscreen-modal-news" content-class="fullscreen-modal" body-class="d-flex flex-column mh-100 h-100 no-gutters p-0 w-100 overflow-hidden" header-class="py-2" footer-class="py-1">
        <!-- En-tête -->
        <template #modal-header="{ close }">           
            <b-col align-self="center" class="pl-0">
                <h6 class="m-0 font-weight-semibold">
                    {{ $t('NewsModal.Title') }} 
                    <b-badge v-if="unreadNotificationsCount() > 0" pill variant="info" class="py-1 px-2 font-weight-normal" >
                        {{ $t("UnreadCount", { count: unreadNotificationsCount()})}}
                    </b-badge>
                </h6>
            </b-col>
            <b-col cols="auto" align-self="center" class="text-right pr-0">
                <b-icon-x-lg role="button" font-scale="1.25" @click="close" />
            </b-col>
        </template>
            <b-row :id="$id('SearchNews')" :class="['h-100 pr-2', {'overflow-auto' : $screen.breakpoint === 'sm' || $screen.breakpoint === 'xs' }]">
                <!-- Fonction de recherche + tableau des notifications -->
                <b-col ref="NewsTableContent" xs="12" sm="12" md="5" class="border-right h-100 pl-3">
                    <!-- Barre de recherche -->
                    <b-row :id="$id('SearchBar')" class="py-3 pl-3 pr-2 bg-white">
                        <b-input-group class="pl-3">
                            <b-form-input v-model="searchValue" autofocus :placeholder="$t('PleaseEnterYourSearch')" @input="filterNews" />
                            <template #prepend>
                                <b-button variant="info"><b-icon icon="search" /></b-button>
                            </template>
                        </b-input-group>
                    </b-row>

                    <!-- Liste des news -->
                    <b-row class="pb-2 pl-3 pr-2">
                        <!-- Tableau selectionable comportant une pagination avec un tri sur la date de publication avec retour sur la première page -->
                        <b-table :ref="$id('NewsTable')" :items="filteredNews" :fields="newsFields" responsive fixed outlined class="m-0 ml-3 rounded overflow-hidden" selectable select-mode="single" :per-page="itemsPerPage" :current-page="currentPage"  @row-clicked="selectNews">
                            <!-- Permet de fixer une largeur pour certaines colonnes -->
                            <template #table-colgroup="scope">
                                <col v-if="scope.fields.filter(field => field.key === 'IsRead')" style="width: 2rem;" />
                                <col v-if="scope.fields.filter(field => field.key === 'PublishDate')" style="width: 6rem;" />
                            </template>

                            <!-- Ligne contenant titre + btn marquer tout comme lu -->
                            <template #thead-top>
                                <b-tr class="bg-info">
                                    <!-- une seule b-th pour réaliser l'adaptation selon le nombre de colonnes du tableau -->
                                    <b-th colspan="3" class="text-white">
                                        <b-row align-h="between">
                                            <b-col>{{ $t('NotificationList') }}</b-col>
                                            <b-col class="font-weight-normal text-right"><u @click="setAllAsRead" role="button">{{ $t('MarkAllAsRead') }}</u></b-col>
                                        </b-row>
                                    </b-th>
                                </b-tr>
                            </template>

                            <!-- Alerte aucun résultat -->
                            <template #empty>
                               <b-tr variant="info">
                                    {{ $t('NoResult') }}
                                </b-tr>
                            </template>

                            <!-- Icone lue/non lue -->
                            <template #cell(IsRead)="data">
                                <b-icon v-if="data.item.IsRead" icon="envelope-open" scale="1.25" />
                                <b-icon v-else icon="envelope-fill" scale="1.25" variant="info" />
                            </template> 

                            <!-- Colonne date -->
                            <template #cell(PublishDate)="data">
                                <p class="m-0">{{ data.item.PublishDate | formatDate('DD/MM/YYYY') }}</p>
                            </template>

                            <!-- Colonne titre -->
                            <template #cell(Title)="data">
                                <p type="button" :class="['m-0 text-truncate', {'font-weight-semibold': !data.item.IsRead}]">{{ data.item.Title }}</p> <!-- @click="openSelectedNews(data.item);" -->  
                            </template>
                        </b-table>
                    </b-row>

                    <!-- Pagination -->
                    <b-pagination
                        v-if="filteredNews.length > 0"
                        v-model="currentPage"
                        :total-rows="filteredNews.length"
                        :per-page="itemsPerPage"
                        
                        hide-goto-end-buttons
                        class="pb-3"
                        align="center"
                        :style="generateVariantStyle(Variant)"
                    /> <!-- @input="onPageChange" -->
                </b-col>
                    
                <!-- Affichage du contenu de la notification -->
                <b-col :id="$id('NewsContent')" xs="12" sm="12" md="7" align-self="center" class="d-flex flex-column mh-100 h-100">
                    <!-- Avec sélection -->
                    <template v-if="selectedNews">
                        <NewsContent :News="selectedNews" :ShowTitle="true" class="overflow-auto px-2 flex-grow-1 w-100 no-gutters" />
                    </template>
                    <!-- Sans sélection -->
                    <template v-else>
                        <b-row align-v="center" class="h-100">
                            <b-col class="text-center">{{ $t('PleaseSelectNotification') }}</b-col>
                        </b-row>
                    </template>
                </b-col>
            </b-row>

        <!-- Pied de page -->
        <template #modal-footer="{ close }">
            <b-row align-h="between" class="d-flex w-100">
                <div></div>
                <!-- Bouton affiché uniquement sur les écrans de type 'smartphone' pour retourner en haut de la modal -->
                <b-button v-if="$screen.breakpoint === 'sm' || $screen.breakpoint === 'xs'" :variant="'outline-'+Variant" pill @click="backToTop()">
                    Retour en haut
                </b-button>

                <!-- Bouton Fermer si on est à la dernière news -->
                <b-button :variant="Variant" pill @click="close">
                    {{ $t('Close') }}
                </b-button>
            </b-row>
        </template>
    </b-modal>
</template>

<script lang="ts">
    import { Vue, Prop, PropSync, Watch } from 'vue-property-decorator';
    import Component, { mixins } from 'vue-class-component';
    import locales from './locales/SearchNews.json';
    import NewsNotification from '@/types/NewsNotification';
    import NewsContent from './NewsContent.vue';
    import { LinkUtils, ThemeUtils } from "@/components/commons/Utils";
    import { Getter } from 'vuex-class';

    @Component({
        components : { NewsContent },
        i18n: { messages: locales }
    })
    export default class SearchNews extends mixins( Vue, LinkUtils, ThemeUtils) {
        /** Variant de couleur à utiliser sur le tableau de bord */
        private Variant: string = 'info';

        /** Liste des notifications provenant du parent */
        @Prop({ default: () => [] }) 
        readonly notifications!: NewsNotification[];

        /** Contrôle d'affichage de la modal searchNews  */
        @PropSync('Visible', { default: () => false }) 
        public visible!: boolean;

        // Pagination
        private currentPage: number = 1;
        private itemsPerPage: number = 5;

        // Valeur de recherche
        private searchValue: string = '';
        private lastSearchValue: string = '';

        // Liste filtrée selon la recherche
        private filteredNews: NewsNotification[] = [];

        /** Colonnes du tableau */
        private newsFields = [
            { key: 'IsRead', label: ""},
            { key: 'PublishDate', label: this.$t('Headers.Date').toString(), sortable: false },
            { key: 'Title', label: this.$t('Headers.Title').toString(), tdClass: 'text-truncate' },        
        ];

        /** Indique le sens de tri du tableau -> false : croissant - true : décroissant  */
        private sortDesc: boolean = true;
        
        /** Nom de la clé de la colonne où s'applique le tri actuel */
        private sortBy: string = "PublishDate";

        // Observateur se déclenchant sur le resize d'un élément
        private resizeObserver: ResizeObserver = null;

        /** News Notification selectionnée */
        @Getter('NotificationsStore/GetSelectedNewsNotification')
        private selectedNews!: NewsNotification | null;

        /** Mise à jour des news lorsqu’on reçoit de nouvelles notifications */
        @Watch('notifications', { immediate: true })
        private onNotificationsChange() {
            this.filterNews();
        }

        /** Méthode de filtrage des actualités */
        private filterNews() {
            const query = this.searchValue.trim().toLowerCase();

            // Met à jour uniquement si la valeur a changé
            if (query !== this.lastSearchValue) {
                this.lastSearchValue = query;
                this.currentPage = 1; // <-- Reset page seulement si recherche a changé
            }

            if (query === '')
                this.filteredNews = this.notifications;
            else {
                this.filteredNews = this.notifications.filter(news =>
                    news.Title.toLowerCase().includes(query)
                );
            }
        }

        /** Methode pour prevenir l'etat de l'affichage du composant parent */
        private async onHide() {
            // On masque la fenêtre et la notification courante
            await this.$store.dispatch('NotificationsStore/HideSearchNews');
            await this.$store.dispatch('NotificationsStore/SetCurrentNewsNotification', null);
        }

        /** Récupération du nombre de notifications qui n'ont pas été lues */
        private unreadNotificationsCount(): number {
            return this.notifications.filter(n => n.IsRead == false).length;
        }

        /** Permet de rafraichir la nombre d'élément par page du tableau par rapport à sa colonne parente */
        private refreshElementPerPage() {
            let col: HTMLElement = (this.$refs["NewsTableContent"] as HTMLElement);

            console.debug("SearchNews - refreshHeight - col", col);

			if (col) {
				// Hauteurs des différentes parties du tableau (header, footer, ligne du tableau)
				let headHeight: number = col.querySelector('thead') ? col.querySelector('thead').clientHeight : 0;
				let footHeight: number = col.querySelector('tfoot') ? col.querySelector('tfoot').clientHeight : 0;
				let lineHeight: number = col.querySelector('tbody tr') ? col.querySelector('tbody tr').clientHeight : 0;

                // Hauteurs de la partie de recherche ainsi que la pagination
                let searchHeight: number = col.querySelector("div[id="+ this.$id('SearchBar') +"]") ? col.querySelector("div[id="+ this.$id('SearchBar') +"]").clientHeight : 0;
                let paginationHeight: number = col.querySelector('ul.pagination') ? col.querySelector('ul.pagination').clientHeight : 0;

				console.debug(`SearchNews - refreshHeight - headHeight : ${headHeight} - footHeight : ${footHeight} - lineHeight : ${lineHeight} - searchHeight : ${searchHeight} - paginationHeight ${paginationHeight}`);

                // Hauteur de la colonne
				let maxHeight: number = col.clientHeight;
				
				if(maxHeight > 0) {
					console.debug("SearchNews - refreshHeight - maxHeight", maxHeight);

					// Calcul du nombre d'éléments par page 
					let calcPerPage: number = (maxHeight - (headHeight + footHeight + searchHeight + paginationHeight)) / lineHeight;

					console.debug(`SearchNews - refreshHeight - height : ${(maxHeight - (headHeight + footHeight + searchHeight + paginationHeight))} - calcPerPage : ${calcPerPage}` );

					// Affectation du nombre de page en recupérant uniquement la partie entière du calcul
					this.itemsPerPage = calcPerPage > 1 ? Math.trunc(calcPerPage) : 1;
				}
			}
        }

        /** Sélection de la news à afficher sur la partie droite */
        private async selectNews(item: NewsNotification) {

            // Étape 1 : Définir la news sélectionnée dans le store 
            await this.$store.dispatch('NotificationsStore/LoadNews', item.Id).then(async () => {
                /** Scrolling vers le preview en bas pour taille smartphone */
                if (this.$screen.breakpoint === 'sm' || this.$screen.breakpoint === 'xs') {
                    const el = document.getElementById(this.$id('NewsContent'));
                    if (el)
                        el.scrollIntoView(true);
                }

                // Étape 2 : marquer cette news comme lue si elle ne l'est pas
                if(!item.IsRead) {
                    // Étape 3 : recharger la liste des notifications (mise à jour côté back avec la news lue)
                    await this.$store.dispatch('NotificationsStore/MarkNewsAsRead').then(async () => {
                        await this.$store.dispatch('NotificationsStore/LoadNewsNotifications', true).then(() => {
                            // Recherche l'index de la news dans la tableau
                            let index: number = this.filteredNews.findIndex(r => r.Id == item.Id);
                            if(index > -1)
                                (this.$refs[this.$id('NewsTable')] as any).selectRow(index - this.itemsPerPage * (this.currentPage - 1));
                        });
                    });
                }
            });
        }

        /** Permet de passer en lue toutes les notifications de l'utilisateur */
        private async setAllAsRead(){
            this.$store.dispatch("ShowLoadingMask", "Mise à jour de toutes les notifications");
            
            // On marque en lue toutes les notifications de l'utilisateur
            await this.$store.dispatch('NotificationsStore/MarkAllNewsAsRead');

            // rafraîchir la liste (sera à jour côté back avec IsRead à true)
            await this.$store.dispatch('NotificationsStore/LoadNewsNotifications', true);

            this.$store.dispatch("HideLoadingMask", {});
        }

        /** Permet de retourner en haut de la modal */
        private backToTop() {
            let modal: HTMLElement = document.getElementById(this.$id('SearchNews'));
            if (modal)
                modal.scrollTo({ top: 0, behavior: 'smooth'});
        }

        mounted() {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    console.debug("SearchNews - mounted - ref NewsTableContent", this.$refs.NewsTableContent);
                    /** Ajout d'un observeur sur le redimensionnement du tableau pour la gestion du nombre d'éléments par page  */
                    this.resizeObserver = new ResizeObserver(this.refreshElementPerPage);
                    this.resizeObserver.observe((this.$refs.NewsTableContent as HTMLElement));
                })
            })
        }

        beforeUnmount() {
            if(this.resizeObserver)
                this.resizeObserver.disconnect();
        }
    }
</script>

<style scoped>
    /** Pour que la page active prenne en compte la couleur de Variant */
	:deep(.page-item.active .page-link) {
		background-color: var(--Variant) !important;
	}

    /** pour la gestion de la taille de la fenêtre (surcharge la classe définie dans App.vue) */
    :deep(.fullscreen-modal-news) {
        height: 90% !important;
		width: 70% !important;
        max-width: 100% !important;
		max-height: var(--fullHeight) !important;
        overflow: hidden !important;
		padding: 1vh !important;
    }
</style>
